import React, { Fragment, useEffect, useState } from "react";
import {
  AddIcon,
  DeleteIcon,
  DownArrowIcon,
  UpArrowIcon,
} from "../../../assets/icons";
import CannedTestSectionGroup from "../../../model/cannedTests/cannedTestSectionGroup";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import PreviewModal from "../questions/previewModal";
import { IQuestion } from "../../../model/interface/question";
import CannedTestSectionGroupQuestion from "../../../model/cannedTests/cannedTestSectionGroupQuestion";
import SectionGroupCardSortableItem from "./sectionGroupCardSortableItem";

interface SectionGroupCardProps {
  groupIndex: number;
  editQuestionHandler: (questionId: number) => void;
  sectionGroup: CannedTestSectionGroup;
  onSectionGroupTypeChange: (data: any, index: number) => void;
  onSectionGroupInstructionChange: (data: any, index: number) => void;
  handleDragEnd: (data: any, groupIndex: number) => void;
  onQuestionChange: (e: any, index: number, groupIndex: number) => void;
  onEditClick: (question: any, index: number) => void;
  onDeleteGroupQuestionClick: (groupIndex: number, index: number) => void;
  onAddGroupQuestionClick: (groupIndex: number) => void;
  isEditIndex: number;
  setIsEditIndex: (isEditIndex: number) => void;
  isEdit: boolean;
  setIsEdit: (isEdit: boolean) => void;
  onDeleteGroupClick: (groupIndex: number) => void;
}

const SectionGroupCard = (props: SectionGroupCardProps) => {
  const [toggleSectionGroup, setToggleSectionGroup] = useState<boolean>(true);
  const [sectionGroup, setSectionGroup] = useState<CannedTestSectionGroup>(
    props.sectionGroup
  );
  const [open, setOpen] = useState<boolean>(false);
  const [previewId, setPreviewId] = useState<string>("");
  const [questionToPreview, setQuestionToPreview] = useState<
    IQuestion | undefined
  >();
  const [questionIdPreview, setQuestionIdPreview] = useState<number>();
  const [questionIndexPreview, setQuestionIndexPreview] = useState<number>();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onQuestionSubmit = () => {
    props.setIsEdit(false);
    props.setIsEditIndex(-1);
  };

  const openModal = (
    sectionGroupQuestiontion: CannedTestSectionGroupQuestion
  ) => {
    const questionId = sectionGroupQuestiontion.questionId;
    const questionDetail: IQuestion = {
      questionId: questionId,
      englishQuestionReferenceId:
        sectionGroupQuestiontion.englishQuestionReferenceId,
      spanishQuestionReferenceId:
        sectionGroupQuestiontion.spanishQuestionReferenceId,
    };

    setOpen(true);
    setPreviewId(questionDetail?.englishQuestionReferenceId ?? "");
    setQuestionToPreview(questionDetail);
    setQuestionIdPreview(questionId);
  };

  const popUpClose = (value) => {
    setOpen(value);
    setPreviewId("");
    setQuestionIdPreview(undefined);
    setQuestionToPreview(undefined);
  };

  const onCancelClick = () => {
    props.setIsEdit(false);
    props.setIsEditIndex(-1);
  };
  useEffect(() => {
    setSectionGroup(props.sectionGroup);
  }, [props.sectionGroup.sectionGroupQuestions]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || sectionGroup.sectionGroupQuestions.length <= 1) return;
    if (active.id !== over.id) {
      const oldIndex = sectionGroup.sectionGroupQuestions.findIndex(
        (item) => item.questionId.toString() === active.id
      );
      const newIndex = sectionGroup.sectionGroupQuestions.findIndex(
        (item) => item.questionId.toString() === over.id
      );
      const newItems = arrayMove(
        sectionGroup.sectionGroupQuestions,
        oldIndex,
        newIndex
      );
      // Call the parent handler with the new order
      props.handleDragEnd({ ...event, newItems }, props.groupIndex);
    }
  };

  return (
    <Fragment>
      <PreviewModal
        modal={openModal}
        show={open}
        changeValue={popUpClose}
        itemRef={previewId}
        questionIdPreview={questionIdPreview}
        questionIndexPreview={questionIndexPreview}
        setQuestionIndexPreview={setQuestionIndexPreview}
        questions={[]}
        setQuestionIdPreview={setQuestionIdPreview}
        setPreviewId={setPreviewId}
        questionToPreview={questionToPreview}
        setQuestionToPreview={setQuestionToPreview}
      ></PreviewModal>

      <div className="bg-white">
        <div className="shadow border p-2 overflow-hidden rounded-lg mt-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex justify-start items-center">
              {!toggleSectionGroup && (
                <DownArrowIcon
                  className="h-5 w-5 ml-1 text-gray-500"
                  onClick={() => {
                    setToggleSectionGroup(!toggleSectionGroup);
                  }}
                />
              )}
              {toggleSectionGroup && (
                <UpArrowIcon
                  className="h-5 w-5 ml-1 text-gray-500"
                  onClick={() => {
                    setToggleSectionGroup(!toggleSectionGroup);
                  }}
                />
              )}
              <h3 className="ml-2">{sectionGroup.type}</h3>
            </div>
            <div className="flex justify-end items-center">
              <button
                type="button"
                onClick={() => {
                  props.onDeleteGroupClick(props.groupIndex);
                }}
                className="flex my-4 mx-3  items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
              >
                <DeleteIcon className="w-3 h-3"></DeleteIcon>
                <span>Delete Group</span>
              </button>
            </div>
          </div>
          {toggleSectionGroup && (
            <div className={"ml-4 pt-6"}>
              <div className="grid sm:grid-cols-4 md:grid-cols-8  xl:grid-cols-12 gap-6 text-sm">
                <div className="col-span-4">
                  <div className="text-sm">
                    <label className="font-medium text-gray-700">Type</label>
                  </div>
                  <div className={"w-full mt-1"}>
                    <input
                      className="block w-full border border-gray-300 text-gray-700 rounded-md shadow-sm py-2 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm"
                      type="text"
                      value={sectionGroup.type}
                      onChange={(e) =>
                        props.onSectionGroupTypeChange(e, props.groupIndex)
                      }
                    ></input>
                  </div>
                  <div className="col-span-4 mt-5">
                    <div className="text-sm">
                      <label className="font-medium text-gray-700">
                        Instructions
                      </label>
                    </div>
                    <div className={"w-full mt-1"}>
                      <textarea
                        className="block w-full border border-gray-300 h-28 text-gray-700 rounded-md shadow-sm py-2 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm"
                        value={sectionGroup.instructions}
                        onChange={(e) =>
                          props.onSectionGroupInstructionChange(
                            e,
                            props.groupIndex
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="mt-4 mr-4 min-w-full divide-y divide-gray-200">
                  <thead className="bg-primary-color">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white tracking-wider"
                      >
                        <span className="flex">Action</span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 whitespace-nowrap  font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        <span className="flex">Sequence Number</span>
                      </th>
                      <th
                        scope="col"
                        className="inline-flex items-center whitespace-nowrap px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        <span className="flex">Question Id</span>
                      </th>
                      <th></th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={sectionGroup.sectionGroupQuestions.map((q) =>
                      q.questionId.toString()
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {sectionGroup.sectionGroupQuestions.map(
                        (question, index) => (
                          <SectionGroupCardSortableItem
                            key={question.questionId}
                            question={question}
                            index={index}
                            isEdit={props.isEdit}
                            isEditIndex={props.isEditIndex}
                            groupIndex={props.groupIndex}
                            onEditClick={props.onEditClick}
                            onQuestionChange={props.onQuestionChange}
                            onDeleteGroupQuestionClick={
                              props.onDeleteGroupQuestionClick
                            }
                            editQuestionHandler={props.editQuestionHandler}
                            onQuestionSubmit={onQuestionSubmit}
                            onCancelClick={onCancelClick}
                            openModal={openModal}
                          />
                        )
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          )}
          {toggleSectionGroup && (
            <div>
              <div className="flex justify-end w-full border-t mt-6">
                <button
                  onClick={() =>
                    props.onAddGroupQuestionClick(props.groupIndex)
                  }
                  type="button"
                  className="flex my-4 mx-3  items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                >
                  <AddIcon className="w-3 h-3"></AddIcon>
                  <span>Add Question</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default SectionGroupCard;
